import { useRecaptcha } from '@bepro-travel/fe.hooks';
import React, { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SearchRuToursModel } from '../../ts/classes/SearchRuToursModel';
import OphirSearchBox from '../OphirSearchBox';
import { OphirResultsResponse } from '../../ts/interfaces/OphirResponse';
import ResultsList from './ResultsList';
import { OphirFilterModel } from '../../ts/classes/OphirFilterModel';
import OphirFilter from './OphirFilter';
import { filtering, getImgUrl, sorting } from '../../utils';
import { Select } from '@bepro-travel/fe.ui';
import OphirSorter from './OphirSorter';
import OphirResultsSkeleton from './OphirResultsSkeleton';
import { dateFormat } from '@bepro-travel/fe.utils';
import { search } from '../../service';
import { useTranslation } from 'react-i18next';
import { cdnUrl } from '../../constants';

const OphirResultsPage = () => {
  const [t] = useTranslation() as unknown as [(key: string) => string];
  const { handleRecapcha, isRecaptchaReady } = useRecaptcha();
  const [urlParams] = useSearchParams();
  const [response, setResponse] = useState<OphirResultsResponse>();
  const [filter, setFilter] = useState<OphirFilterModel>();
  const [country, setCountry] = useState<string>();
  const checkIn = (urlParams.get('checkIn') || '').split('-');
  const bgImg = sessionStorage.getItem('sliderFirstImg');

  useEffect(() => {
    setResponse(undefined);
    if (!isRecaptchaReady) return;

    (async () => {
      const recapcha = await handleRecapcha();
      if (!recapcha) return;

      const params = new SearchRuToursModel(urlParams);
      const newResponse = await search({ ...params, recaptchaToken: recapcha });

      if (newResponse) {
        const newPackages = newResponse.tourPackageDeals.map((tour, i) => ({
          ...tour,
          visible: true,
          index: i,
        }));
        setResponse({
          ...newResponse,
          tourPackageDeals:
            newPackages.sort(
              (a, b) =>
                new Date(
                  new Date(a.checkIn.replace(/(\d{2})\.(\d{2})\.(\d{2})/, '20$3-$2-$1'))
                ).getTime() -
                new Date(b.checkIn.replace(/(\d{2})\.(\d{2})\.(\d{2})/, '20$3-$2-$1')).getTime()
            ) || [],
        });
      }
    })();
  }, [isRecaptchaReady, urlParams]); // eslint-disable-line

  useEffect(() => {
    if (response) {
      const countriesStr = sessionStorage.getItem('ophCountries');
      const countries: Select[] = countriesStr ? JSON.parse(countriesStr) : [];
      const monthsStr = sessionStorage.getItem('ophMonths');
      const months: Select[] = monthsStr ? JSON.parse(monthsStr) : [];
      const newFilter = new OphirFilterModel(response.tourPackageDeals, countries, months);
      setFilter(newFilter);
      const choosenCountry = countries.find((c) => c.value === urlParams.get('countryCode'));
      setCountry(choosenCountry?.label!);
    }
  }, [response?.tourPackageDeals.length]); // eslint-disable-line

  const onFilterChange = (newFilter: OphirFilterModel) => {
    const newPackages = filtering(newFilter, response?.tourPackageDeals || []);

    setFilter(newFilter);
    setResponse({ ...response!, tourPackageDeals: newPackages });
  };

  const onClickReset = () => {
    const countriesStr = sessionStorage.getItem('ophCountries');
    const countries: Select[] = countriesStr ? JSON.parse(countriesStr) : [];
    const monthsStr = sessionStorage.getItem('ophMonths');
    const months: Select[] = monthsStr ? JSON.parse(monthsStr) : [];
    const newFilter = new OphirFilterModel(response?.tourPackageDeals || [], countries, months);
    const newPackages =
      response?.tourPackageDeals?.map((tour) => ({ ...tour, visible: true })) || [];

    setFilter(newFilter);
    setResponse({ ...response!, tourPackageDeals: newPackages });
  };

  const onSort = (sortModel: any) => {
    const newPackages = sorting(sortModel, response!.tourPackageDeals);

    setResponse({ ...response!, tourPackageDeals: newPackages });
  };

  return (
    <div className="results-main">
      <div
        className="results-main-cover"
        style={{ backgroundImage: `url(${getImgUrl(bgImg || '', cdnUrl)})` }}
      ></div>
      <OphirSearchBox searchBox="" />
      {!response?.hasErrors && !!response?.tourPackageDeals?.length && (
        <div className="packages-result-wr results-wr oph-results-wr">
          <div className="oph-filter-wr">
            <div className="mb-30">
              <h2 className="text-bold text-lg mb-5">
                {urlParams.get('allCountries') === 'true'
                  ? t('tour.all')
                  : `${t('tour.in')} ${country}`}
              </h2>
              <div>
                {`${response.tourPackageDeals.length} ${t('tour.options')} · ${checkIn.length === 3
                  ? dateFormat(
                    new Date(
                      parseInt(checkIn[2]),
                      parseInt(checkIn[1]) - 1,
                      parseInt(checkIn[0])
                    ),
                    'MMMM'
                  )
                  : t('tour.all')
                  }`}
              </div>
            </div>
            {filter && (
              <div className="mb-30">
                <OphirFilter
                  filter={filter}
                  onChange={onFilterChange}
                  onReset={onClickReset}
                  onChangePrice={setFilter}
                />
              </div>
            )}
          </div>
          <div className="package-list results-list">
            <OphirSorter onChange={onSort} />
            <ResultsList
              list={response.tourPackageDeals.filter((tour) => tour.visible)}
              searchToken={response.searchToken}
            />
          </div>
        </div>
      )}
      {!response && <OphirResultsSkeleton />}

      {!response?.hasErrors && !response?.tourPackageDeals?.length && (
        <div className="mt-30 w100p text-center">{t('no_records')}</div>
      )}
    </div>
  );
};

export default OphirResultsPage;
